import { inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { patchState, signalStore, withMethods } from '@ngrx/signals';
import {
  entityConfig,
  upsertEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { ResultService } from '@thiememeulenhoff/ng-api-gateway';
import { BulkDeleteCollection, GroupResult } from '@thiememeulenhoff/types';
import {
  RequestStatus,
  withRequestStatus,
} from '../_utils/request-status.feature';

export function groupResultIdentifier(
  groupCode: string,
  arrangementCode: string,
  contentIdentifier: string,
  showTeachers: boolean,
): string {
  return `${groupCode}_${arrangementCode}_${contentIdentifier}_${showTeachers}`;
}

const groupResultConfig = entityConfig({
  entity: {} as GroupResult,
  selectId: (e: GroupResult) => e.identifier,
});

export const ResultStore = signalStore(
  { providedIn: 'root' },
  withEntities(groupResultConfig),
  withRequestStatus(),
  withMethods((store, resultService = inject(ResultService)) => ({
    async fetchGroupResult(
      groupCode: string,
      arrangementCode: string,
      contentIdentifier: string,
      showTeachers: boolean,
    ): Promise<void> {
      patchState(store, { requestStatus: 'loading' as RequestStatus });
      try {
        const result = await firstValueFrom(
          resultService.fetchGroupResult(
            groupCode,
            arrangementCode,
            contentIdentifier,
            showTeachers,
          ),
        );
        patchState(
          store,
          upsertEntity(
            {
              ...result,
              identifier: groupResultIdentifier(
                groupCode,
                arrangementCode,
                contentIdentifier,
                showTeachers,
              ),
            },
            groupResultConfig,
          ),
          { requestStatus: 'success' as RequestStatus },
        );
      } catch (e: unknown) {
        const error =
          e instanceof Error ? e.message : 'Failed to fetch group result';
        patchState(store, { requestStatus: { error } as RequestStatus });
        throw e;
      }
    },
    async deleteGroupResult(
      groupCode: string,
      collection: BulkDeleteCollection,
    ): Promise<void> {
      await firstValueFrom(
        resultService.bulkDeleteGroupResult(groupCode, collection),
      );
    },
  })),
);
